import * as React from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody, Typography, makeStyles, Button, Collapse, Grid } from '@material-ui/core';
import { useIifImportSelector, IifImportSelectors } from '../store/modules/iifImport';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { DateTime } from 'luxon'
import { classNames } from '../shared';
import ExpenseDistributionViews from './ExpenseDistributionsView';
import AudCurrencyFormatter from '../utilities/currencyFormatter';
import { useApplicationSettingsSelector, ApplicationSettingsSelectors, TaxMode } from '../store/modules/applicationSettings';
import { yellow, red, green } from '@material-ui/core/colors';
import AttachmentViews from './AttachmentsView';

interface IExpenseViewProps {
    fileIndex: number;
    expenseIndex: number;
    expensePosition: number;
    importSuccess: boolean;
    importError: boolean;
}

const useStyles = makeStyles(theme => ({
    error: {
        background: `${red[100]} !important`
    },
    financialError: {
        background: `${theme.palette.warning.dark} !important`,
        color: `${theme.palette.common.white} !important`
    },
    warning: {
        '& td': {
            background: yellow[100]
        }
    },
    errorRow: {
        '& td': {
            background: red[100]
        }
    },
    successRow: {
        '& td': {
            background: green[100]
        }
    },
    button: {
        textAlign: 'center',
        minWidth: 'auto',
        padding: theme.spacing(1),

        '& svg': {
            transition: 'all 0.25s linear'
        }
    },
    expanded: {
        '& svg': {
            transform: 'rotate(180deg)'
        }
    },
    noPadding: {
        padding: theme.spacing(0),
        paddingRight: `${theme.spacing(0)} !important`
    },
    padding: {
        padding: theme.spacing(2)
    }
}))

const ExpenseView: React.FunctionComponent<IExpenseViewProps> = ({
    fileIndex,
    expenseIndex,
    expensePosition,
    importError,
    importSuccess
}) => {
    const expense = useIifImportSelector(state => IifImportSelectors.selectExpense(state, expenseIndex));

    const taxMode = useApplicationSettingsSelector(ApplicationSettingsSelectors.selectTaxMode);

    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const onExpandClick = React.useCallback(() => {
        if (expanded) {
            setShow(!show);
        }
        else {
            setExpanded(true);
            setShow(true);
        }
    }, [setShow, show, expanded, setExpanded]);

    const onExited = React.useCallback(() => {
        setExpanded(false);
    }, [setExpanded])

    if (!expense) {
        return null;
    }

    return (
        <>
            <TableRow className={classNames({ [classes.warning]: expense.distributionErrors || expense.attachmentErrors || !expense.attachmentsDownloaded || expense.financialYearError }, { [classes.successRow]: importSuccess }, { [classes.errorRow]: importError })}>
                <TableCell>
                    <Button
                        className={classNames(classes.button, { [classes.expanded]: show })}
                        color="inherit"
                        onClick={onExpandClick}
                    >
                        <FontAwesomeIcon icon={faChevronDown} fixedWidth />
                    </Button>
                </TableCell>
                <TableCell>{expensePosition}</TableCell>
                <TableCell className={classNames({ [classes.financialError]: expense.financialYearError })}>{expense.transactionDate ? DateTime.fromISO(expense.transactionDate).toFormat('dd/MM/yyyy') : null}</TableCell>
                <TableCell className={classNames({ [classes.error]: expense.accountError })}>{expense.account?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: expense.vendorError })}>{expense.vendor?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: expense.paymentMethodError })}>{expense.paymentMethod?.value}</TableCell>
                <TableCell>{expense.docNumber}</TableCell>
                <TableCell className={classNames({ [classes.error]: expense.currencyError })}>{expense.currency?.value}</TableCell>
                <TableCell className={classNames({ [classes.error]: expense.amountError })} align="right">
                    {
                        taxMode === TaxMode.Excluded ?
                            AudCurrencyFormatter.format(expense.distributionAmount ?? 0) :
                            AudCurrencyFormatter.format(expense.distributionTotal ?? 0)
                    }
                </TableCell>
            </TableRow>
            {
                expanded && (
                    <TableRow>
                        <TableCell className={classes.noPadding} />
                        <TableCell colSpan={8} className={classes.noPadding}>
                            <Collapse in={show} appear={true} onExited={onExited} >
                                <div className={classes.padding}>
                                    {
                                        expense.remitToAddress && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={3} lg={2}>
                                                    <Typography>
                                                        <strong>
                                                            Address
                                                </strong>
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    {
                                                        expense.remitToAddress.line1 && (
                                                            <Typography>
                                                                {expense.remitToAddress.line1}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        expense.remitToAddress.line2 && (
                                                            <Typography>
                                                                {expense.remitToAddress.line2}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        expense.remitToAddress.line3 && (
                                                            <Typography>
                                                                {expense.remitToAddress.line3}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        expense.remitToAddress.line4 && (
                                                            <Typography>
                                                                {expense.remitToAddress.line4}
                                                            </Typography>
                                                        )
                                                    }
                                                    {
                                                        expense.remitToAddress.line5 && (
                                                            <Typography>
                                                                {expense.remitToAddress.line5}
                                                            </Typography>
                                                        )
                                                    }
                                                </Grid>
                                            </Grid>
                                        )
                                    }
                                </div>
                                <ExpenseDistributionViews
                                    distributionView={expense.distributionView}
                                    amount={expense.distributionAmount ?? 0}
                                    tax={expense.distributionTax ?? 0}
                                    total={expense.distributionTotal ?? 0}
                                />
                                <AttachmentViews
                                    fileIndex={fileIndex}
                                    type="expenses"
                                    typeIndex={expenseIndex}
                                    attachmentView={expense.attachmentView}
                                />
                            </Collapse>
                        </TableCell>
                    </TableRow>
                )
            }
        </>
    );
}

export default ExpenseView;


// WEBPACK FOOTER //
// ./src/components/ExpenseView.tsx